/**
 * TreeTestResultPanel (TASK-260)
 *
 * Result panel for a manual tree-test calculation (FR-004 / AC-4):
 *   - line items table (name / unit / quantity / unit_price / total_price),
 *   - subtotal / overhead / total,
 *   - an expandable per-node trace (formula + intermediate values), collapsed
 *     by default — the trace body is mounted ONLY after its trigger is clicked.
 *
 * Numbers are rendered with ru-RU thousands separators; tests match on the digit
 * sequence only, so formatting stays free to change.
 */
import { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';

import type {
  TreeTestResult,
  TreeTestTraceNode,
} from '@/services/api/services';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

export interface TreeTestResultPanelProps {
  result: TreeTestResult | null;
}

// Format a number with ru-RU grouping. Falsy/NaN guard keeps the cell readable.
function fmt(value: number): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  return value.toLocaleString('ru-RU');
}

// Render an inputs/intermediate map as readable «key = value» lines.
function KeyValueList({
  data,
}: {
  data: Record<string, unknown>;
}): JSX.Element | null {
  const entries = Object.entries(data ?? {});
  if (entries.length === 0) return null;
  return (
    <ul className="text-sm text-gray-700 space-y-0.5">
      {entries.map(([key, value]) => (
        <li key={key}>
          <span className="text-gray-500">{key}</span> ={' '}
          <span className="font-mono">
            {typeof value === 'number' ? fmt(value) : String(value)}
          </span>
        </li>
      ))}
    </ul>
  );
}

// A single expandable trace row. Collapsed by default — the body (formula +
// intermediate values) mounts ONLY once expanded, so the formula text is absent
// from the DOM until the user opens it.
function TraceRow({ node }: { node: TreeTestTraceNode }): JSX.Element {
  const [open, setOpen] = useState(false);

  return (
    <div className="rounded-md border">
      <button
        type="button"
        data-testid={`tree-test-trace-trigger-${node.service_id}`}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm font-medium text-gray-800 hover:bg-gray-50"
      >
        {open ? (
          <ChevronDown className="h-4 w-4 text-gray-400" />
        ) : (
          <ChevronRight className="h-4 w-4 text-gray-400" />
        )}
        <span className="truncate">{node.service_name}</span>
        <span className="ml-auto font-mono text-gray-500">{fmt(node.result)}</span>
      </button>

      {open && (
        <div
          data-testid={`tree-test-trace-content-${node.service_id}`}
          className="space-y-3 border-t bg-gray-50/50 px-3 py-3"
        >
          <div>
            <div className="text-xs font-medium text-gray-500 mb-1">Формула</div>
            <code className="block rounded border bg-white px-2 py-1 text-sm font-mono text-gray-800">
              {node.formula}
            </code>
          </div>

          {Object.keys(node.inputs ?? {}).length > 0 && (
            <div>
              <div className="text-xs font-medium text-gray-500 mb-1">
                Входные значения
              </div>
              <KeyValueList data={node.inputs} />
            </div>
          )}

          {Object.keys(node.intermediate ?? {}).length > 0 && (
            <div>
              <div className="text-xs font-medium text-gray-500 mb-1">
                Промежуточные значения
              </div>
              <KeyValueList data={node.intermediate} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function TreeTestResultPanel({
  result,
}: TreeTestResultPanelProps): JSX.Element {
  // Empty placeholder for a null OR empty (no line items) result. We still
  // render the root container so the panel keeps a stable mount point.
  const isEmpty = !result || result.line_items.length === 0;

  if (isEmpty) {
    return (
      <div
        data-testid="tree-test-result-panel"
        className="rounded-lg border bg-white shadow-sm p-6"
      >
        <p
          data-testid="tree-test-result-empty"
          className="text-center text-sm text-gray-400"
        >
          Нет результата. Выберите услуги и нажмите «Рассчитать».
        </p>
      </div>
    );
  }

  return (
    <div
      data-testid="tree-test-result-panel"
      className="rounded-lg border bg-white shadow-sm p-4 space-y-6"
    >
      {/* Line items */}
      <div className="overflow-x-auto">
        <Table data-testid="tree-test-line-items">
          <TableHeader>
            <TableRow>
              <TableHead>Наименование</TableHead>
              <TableHead>Ед.</TableHead>
              <TableHead className="text-right">Кол-во</TableHead>
              <TableHead className="text-right">Цена</TableHead>
              <TableHead className="text-right">Итого</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {result.line_items.map((item, idx) => (
              <TableRow key={`${item.name}-${idx}`}>
                <TableCell className="text-gray-800">{item.name}</TableCell>
                <TableCell className="text-gray-600">{item.unit}</TableCell>
                <TableCell className="text-right">{fmt(item.quantity)}</TableCell>
                <TableCell className="text-right">{fmt(item.unit_price)}</TableCell>
                <TableCell className="text-right font-medium">
                  {fmt(item.total_price)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Totals */}
      <div className="space-y-1 border-t pt-3 text-sm">
        <div className="flex items-center justify-between">
          <span className="text-gray-500">Подитог</span>
          <span data-testid="tree-test-subtotal" className="font-mono">
            {fmt(result.subtotal)} ₽
          </span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-gray-500">Накладные</span>
          <span data-testid="tree-test-overhead" className="font-mono">
            {fmt(result.overhead)} ₽
          </span>
        </div>
        <div className="flex items-center justify-between text-base font-semibold">
          <span>Итого</span>
          <span data-testid="tree-test-total" className="font-mono">
            {fmt(result.total)} ₽
          </span>
        </div>
      </div>

      {/* Warnings */}
      {result.warnings.length > 0 && (
        <ul
          data-testid="tree-test-warnings"
          className="space-y-1 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800"
        >
          {result.warnings.map((w, idx) => (
            <li key={idx}>{w}</li>
          ))}
        </ul>
      )}

      {/* Trace */}
      {result.trace.length > 0 && (
        <div className="space-y-2">
          <div className="text-sm font-semibold text-gray-700">
            Трасса расчёта
          </div>
          <div className="space-y-1.5">
            {result.trace.map((node) => (
              <TraceRow key={`${node.service_id}-${node.node}`} node={node} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default TreeTestResultPanel;
